import React from "react";
import { motion } from "framer-motion";
import { GraduationCap, Award, Stethoscope, Quote, MapPin, CheckCircle2 } from "lucide-react";

const fellowships = [
  "Fellowship in Sports Medicine (RGUHS)",
  "Fellowship in Joint Replacement (FIJR)",
  "Fellowship in Upper Limb Arthroscopy (South Korea)",
  "Fellowship in Pain Management (FIPM)",
  "Fellowship in Arthroscopy",
];

export default function DoctorSection({ doctorImage }) {
  return (
    <section id="doctor" className="py-20 md:py-28 bg-slate-50 relative overflow-hidden" data-testid="doctor-section">
      <div className="absolute inset-0 dotted-bg opacity-40" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">
            Meet Your Doctor
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#1E3A8A] leading-tight" style={{ fontFamily: "Outfit" }}>
            Decade of expertise.<br />
            <span className="text-emerald-600">In your pocket.</span>
          </h2>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-3xl p-6 sm:p-10 lg:p-12 shadow-xl shadow-blue-900/5 border border-slate-100"
          data-testid="doctor-card"
        >
          <div className="grid md:grid-cols-5 gap-8 lg:gap-12 items-center">
            {/* Photo */}
            <div className="md:col-span-2 flex justify-center md:justify-start">
              <div className="relative">
                <div className="absolute -inset-3 rounded-full bg-gradient-to-br from-blue-200 via-emerald-200 to-orange-200 blur-xl opacity-60" />
                <div className="relative w-56 h-56 sm:w-64 sm:h-64 lg:w-72 lg:h-72 rounded-full overflow-hidden bg-blue-50 ring-4 ring-white shadow-2xl">
                  <img
                    src={doctorImage}
                    alt="Dr. Akshay Dhanda - Orthopedic Surgeon"
                    className="w-full h-full object-cover"
                    data-testid="doctor-photo"
                  />
                </div>
                <div className="absolute -bottom-2 -right-2 bg-emerald-500 rounded-full p-3 ring-4 ring-white">
                  <Stethoscope className="w-6 h-6 text-white" strokeWidth={2.2} />
                </div>
              </div>
            </div>

            {/* Info */}
            <div className="md:col-span-3 text-center md:text-left">
              <h3
                className="text-2xl sm:text-3xl lg:text-4xl font-black text-[#1E3A8A]"
                style={{ fontFamily: "Outfit" }}
                data-testid="doctor-name"
              >
                Dr. Akshay Dhanda
              </h3>
              <p className="text-base sm:text-lg text-emerald-600 font-semibold mt-1">
                Orthopedic Surgeon · Jayanagar, Bengaluru
              </p>

              <div className="flex flex-wrap justify-center md:justify-start gap-3 mt-4">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100">
                  <GraduationCap className="w-4 h-4 text-blue-700" />
                  <span className="text-xs sm:text-sm font-semibold text-blue-900">MBBS, MS Ortho, DNB Ortho</span>
                </div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-100">
                  <Award className="w-4 h-4 text-emerald-600" />
                  <span className="text-xs sm:text-sm font-semibold text-emerald-800">10+ Years Experience</span>
                </div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-50 border border-orange-100">
                  <MapPin className="w-4 h-4 text-orange-600" />
                  <span className="text-xs sm:text-sm font-semibold text-orange-800">Manipal Hospital</span>
                </div>
              </div>

              {/* Fellowships */}
              <div className="mt-6 grid sm:grid-cols-2 gap-x-4 gap-y-2 text-left">
                {fellowships.map((f) => (
                  <div key={f} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                    <span className="text-xs sm:text-sm text-slate-700 font-medium leading-snug">{f}</span>
                  </div>
                ))}
              </div>

              <div className="mt-6 relative">
                <Quote className="absolute -top-2 -left-1 w-8 h-8 text-blue-100" />
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed pl-6 italic">
                  "Helping patients reduce chronic back pain with practical daily habits
                  and posture correction. You don't need surgery for most back pain —
                  you need the right routine."
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
